import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, } from '@mui/material';
import { Plus, Trash2, Star, Save } from 'lucide-react';
import { CalendarDate } from '@internationalized/date';
import { useDispatch } from 'react-redux';
import { manageSampleTracker } from '../../redux/sampleTrackerSlice';
import CommonDrawerLayout from '../../common_components/CommonDrawerLayout';
import { TextInput } from '../../common_components/FormInputs';
import IOSSwitch from '../../common_components/IOSSwitch';
import AppButton from '../../common_components/AppButton';
import SingleDatePicker from '../../common_components/daterangepicker/SingleDatePicker';
import useToast from '../../common_components/Toast/useToast';
// ── helpers ──────────────────────────────────────────────────────────────────
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (email) => EMAIL_REGEX.test(email.trim());
const emptyProduct = () => ({
    product: '',
    tracking: '',
    sample_sent_date: null,
    published_date: null,
    rating: null,
    sample_sent: false,
    sample_received: false,
});
const toCalendarDate = (val) => {
    if (!val)
        return null;
    try {
        const d = new Date(val);
        if (isNaN(d.getTime()))
            return null;
        return new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
    }
    catch {
        return null;
    }
};
const fromCalendarDate = (val) => {
    if (!val)
        return null;
    return val.toString(); // 'YYYY-MM-DD'
};
// ── Star Rating ───────────────────────────────────────────────────────────────
const StarRating = ({ value, onChange }) => {
    const [hovered, setHovered] = useState(null);
    const display = hovered ?? value ?? 0;
    return (_jsx(Box, { sx: { display: 'flex', gap: '4px', alignItems: 'center' }, children: [1, 2, 3, 4, 5].map((star) => (_jsx(Star, { size: 18, fill: star <= display ? '#F59E0B' : 'none', stroke: star <= display ? '#F59E0B' : '#D1D5DB', style: { cursor: 'pointer', flexShrink: 0 }, onMouseEnter: () => setHovered(star), onMouseLeave: () => setHovered(null), onClick: () => onChange(value === star ? null : star) }, star))) }));
};
// ── Section Card ──────────────────────────────────────────────────────────────
const SectionCard = ({ title, children, headerRight }) => (_jsxs(Box, { sx: { bgcolor: '#fff', borderRadius: '8px', border: '1px solid #EAECF0', p: 2.5 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }, children: [_jsx(Typography, { sx: { fontSize: '14px', fontWeight: 700, color: '#EE2A7B' }, children: title }), headerRight] }), children] }));
// ── Field Label ───────────────────────────────────────────────────────────────
const FieldLabel = ({ children, required }) => (_jsxs(Typography, { component: "label", sx: { display: 'block', fontSize: '13px', fontWeight: 500, color: '#344054', mb: '6px' }, children: [children, required && _jsx("span", { style: { color: '#F04438', marginLeft: 2 }, children: "*" })] }));
const ProductCard = ({ product, index, showDelete, onChange, onDelete }) => (_jsxs(Box, { children: [index > 0 && _jsx(Box, { sx: { borderTop: '1px solid #EAECF0', my: 2 } }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 3, mb: 2, flexWrap: 'wrap' }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Typography, { sx: { fontSize: '13px', fontWeight: 500, color: '#344054' }, children: "Sample Sent" }), _jsx(IOSSwitch, { checked: product.sample_sent, onChange: e => onChange('sample_sent', e.target.checked) })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Typography, { sx: { fontSize: '13px', fontWeight: 500, color: '#344054' }, children: "Sample Received" }), _jsx(IOSSwitch, { checked: product.sample_received, onChange: e => onChange('sample_received', e.target.checked) })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Typography, { sx: { fontSize: '13px', fontWeight: 500, color: '#344054' }, children: "Rating" }), _jsx(StarRating, { value: product.rating, onChange: v => onChange('rating', v) })] }), showDelete && (_jsx(Box, { sx: { ml: 'auto' }, children: _jsx(IconButton, { size: "small", onClick: onDelete, sx: {
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            width: 32,
                            height: 32,
                            color: '#FDA29B',
                            '&:hover': { bgcolor: '#FEF3F2', color: '#D92D20', borderColor: '#FDA29B' },
                        }, children: _jsx(Trash2, { size: 14 }) }) }))] }), _jsxs(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px', mb: 2 }, children: [_jsxs(Box, { children: [_jsx(FieldLabel, { children: "Product" }), _jsx(TextInput, { value: product.product, onChange: e => onChange('product', e.target.value), placeholder: "Product" })] }), _jsxs(Box, { children: [_jsx(FieldLabel, { children: "Tracking" }), _jsx(TextInput, { value: product.tracking, onChange: e => onChange('tracking', e.target.value), placeholder: "222" })] })] }), _jsxs(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px' }, children: [_jsxs(Box, { children: [_jsx(FieldLabel, { children: "Sample Sent Date" }), _jsx(SingleDatePicker, { value: toCalendarDate(product.sample_sent_date), onChange: d => onChange('sample_sent_date', fromCalendarDate(d)), placeholder: "Jan 06, 2026", allowToday: true })] }), _jsxs(Box, { children: [_jsx(FieldLabel, { children: "Published Date" }), _jsx(SingleDatePicker, { value: toCalendarDate(product.published_date), onChange: d => onChange('published_date', fromCalendarDate(d)), placeholder: "Jan 06, 2026", allowToday: true })] })] })] }));
const SampleTrackerDrawer = ({ open, onClose, record, clientId, onSuccess, }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const isEdit = !!record;
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [link, setLink] = useState('');
    const [products, setProducts] = useState([emptyProduct()]);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!open)
            return;
        if (record) {
            setName(record.name);
            setEmail(record.email_address);
            setLink(record.social_website_link);
            setProducts(record.products.length > 0 ? record.products.map(p => ({ ...p })) : [emptyProduct()]);
        }
        else {
            setName('');
            setEmail('');
            setLink('');
            setProducts([emptyProduct()]);
        }
        setEmailError('');
    }, [open, record]);
    const updateProduct = (index, field, value) => {
        setProducts(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
    };
    const removeProduct = (index) => {
        setProducts(prev => prev.filter((_, i) => i !== index));
    };
    const handleSave = async () => {
        if (!name.trim()) {
            toast.error('Influencer/Contact name is required.');
            return;
        }
        if (email.trim() && !isValidEmail(email)) {
            setEmailError('Please enter a valid email address.');
            toast.error('Please enter a valid email address.');
            return;
        }
        setEmailError('');
        setSaving(true);
        try {
            const productPayload = products
                .filter(p => p.product.trim() || p.tracking.trim() || p.sample_sent_date || p.published_date)
                .map(p => ({
                ...(p.id ? { id: p.id } : {}),
                product: p.product,
                tracking: p.tracking,
                sample_sent_date: p.sample_sent_date || null,
                published_date: p.published_date || null,
                rating: p.rating,
                sample_sent: p.sample_sent,
                sample_received: p.sample_received,
            }));
            const payload = {
                action: isEdit ? 'update' : 'create',
                name: name.trim(),
                email_address: email.trim(),
                social_website_link: link.trim() || 'https://google.com',
                client_id: clientId || null,
                products: productPayload,
            };
            if (isEdit)
                payload.id = record.id;
            await dispatch(manageSampleTracker(payload)).unwrap();
            toast.success(isEdit ? 'Record updated.' : 'Record created.');
            onSuccess();
            onClose();
        }
        catch (err) {
            toast.error(err || 'Something went wrong.');
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: "Sample Tracker", width: "md", headerActions: _jsxs(AppButton, { onClick: handleSave, variant: "contained", disabled: saving, sx: {
                bgcolor: '#EE2A7B',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                px: 2.5,
                py: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                '&:hover': { bgcolor: '#D91F6A' },
            }, children: [_jsx(Save, { size: 15 }), saving ? 'Saving...' : 'Save'] }), children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5, p: '8px' }, children: [_jsx(SectionCard, { title: "Information", children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '14px' }, children: [_jsxs(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px' }, children: [_jsxs(Box, { children: [_jsx(FieldLabel, { required: true, children: "Influencer/Contact Name" }), _jsx(TextInput, { value: name, onChange: e => setName(e.target.value), placeholder: "Influencer/Contact Name" })] }), _jsxs(Box, { children: [_jsx(FieldLabel, { children: "Email Address" }), _jsx(TextInput, { value: email, onChange: e => { setEmail(e.target.value); if (emailError)
                                                    setEmailError(''); }, onBlur: () => setEmailError(email.trim() && !isValidEmail(email) ? 'Please enter a valid email address.' : ''), placeholder: "e.g. lorem@gmail.com", type: "email", leftIcon: _jsx("span", { style: { fontSize: 14 }, children: "\u2709" }), error: !!emailError, errorMessage: emailError })] })] }), _jsxs(Box, { children: [_jsx(FieldLabel, { children: "Social/Website Link" }), _jsx(TextInput, { value: link, onChange: e => setLink(e.target.value), placeholder: "https://google.com" })] })] }) }), _jsxs(SectionCard, { title: "Add Sample Tracking", children: [products.map((p, idx) => (_jsx(ProductCard, { product: p, index: idx, showDelete: products.length > 1, onChange: (field, value) => updateProduct(idx, field, value), onDelete: () => removeProduct(idx) }, idx))), _jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-end', mt: 2.5 }, children: _jsx(Button, { onClick: () => setProducts(prev => [...prev, emptyProduct()]), variant: "contained", size: "small", startIcon: _jsx(Plus, { size: 14 }), sx: {
                                    bgcolor: '#EE2A7B',
                                    color: '#fff',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    px: 2,
                                    '&:hover': { bgcolor: '#D91F6A' },
                                }, children: "Add Product" }) })] })] }) }));
};
export default SampleTrackerDrawer;
